import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldErrors, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { NextType } from '../routes/Register.route';
import { registerSchema, type RegisterSchema } from '../schemas/register.schema';
import { apiCall } from '../utils/apiCall';

export const clsInput =
    'bg-white form-input border-transparent w-full placeholder:text-dark text-grey text-xs px-3 py-2.5 mb-4';

const SetPassword = ({ next }: NextType) => {
    const { t } = useTranslation();
    const [error, setError] = useState<null | string>(null);

    const { handleSubmit, register } = useForm<RegisterSchema>({
        mode: 'onChange',
        resolver: zodResolver(registerSchema(t)),
    });
    const onSubmit: SubmitHandler<RegisterSchema> = async ({ password }) => {
        try {
            const response = await apiCall({
                body: JSON.stringify({ password }),
                headers: { 'Content-Type': 'application/json' },
            });

            if (response.status === 200) {
                const body = await response.json();
                if (body.success) {
                    next();
                } else {
                    setError(await response.text());
                }
            } else {
                const errorText = await response.text();
                setError(errorText);
            }
        } catch (err) {
            if (err instanceof Error && err.message.includes('404')) {
                const error404 = t('errorPage.error');
                setError(error404);
                throw err;
            }
        }
    };
    const onError = (errors: FieldErrors<RegisterSchema>) => {
        if (errors?.password) {
            return setError(errors.password.message || 'Invalid password');
        }
        if (errors?.passwordRepeat) {
            return setError(errors.passwordRepeat.message || 'Invalid password repeat');
        }
        return setError('Unknown');
    };

    const handleChange = () => {
        if (error) {
            setError(null);
        }
    };

    return (
        <div className="flex items-center justify-center w-full min-h-screen p-4">
            <div className="w-full max-w-[420px] p-4 mx-auto bg-grey-light">
                <h2 className="mb-4 text-3xl font-bold text-center text-blue">
                    {t('users.registration.WINDOW_TITLE')}
                </h2>
                <form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    className="w-full  flex flex-col bg-gray-100 pt-6 px-6"
                >
                    <div>
                        <input
                            className={clsInput}
                            id="password"
                            type="password"
                            placeholder={t('FORM_FIELDS.PASSWORD')}
                            {...register('password')}
                            data-testid="password-input"
                            onChange={handleChange}
                        />
                    </div>
                    {/* {errors.password && <p className="text-red-500">{errors.password.message}</p>} */}
                    <div>
                        <input
                            className={clsInput}
                            id="passwordRepeat"
                            type="password"
                            placeholder={t('FORM_FIELDS.PASSWORD_REPEAT')}
                            {...register('passwordRepeat')}
                            data-testid="password-repeat-input"
                            onChange={handleChange}
                        />
                    </div>
                    {/* {errors.passwordRepeat && <p className="text-red-500">{errors.passwordRepeat.message}</p>} */}
                    {error ? <p className="text-red-500 ">{error}</p> : null}

                    <input
                        className="text-xs font-semibold bg-blue py-2 px-3.5 text-white"
                        type="submit"
                        value={t('users.registration.SUBMIT_STEP')}
                        data-testid="submit-button"
                    />
                </form>
            </div>
        </div>
    );
};
export default SetPassword;
